// PublishStepsPanel - 公開（完成）準備パネル（spec.md §5.8.3）
// サイドメニュー型: 右辺に常駐し、上端ヘッダーの chevron で collapse/expand。
// collapsed では正方形アイコンの縦並びだけ、expanded で進捗リング・ラベル・
// アクションボタン・フッターを全表示。アイコンは Material Symbols。
// 既存 RightPanel（アセット選択時操作）とは排他表示。

import { useState } from "react";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import ShareIcon from "@mui/icons-material/Share";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { RootState } from "app/store";
import I18n from "common/i18n";
import { ConfirmMessageBox } from "common/elements";
import { useScale, usePlaySound, btnDelay } from "common/utils";
import { CompletionStepId, PieceStatus } from "features/builder/types";
import { completePiece, publishPiece, deleteDraft } from "features/builder/api";
import {
  actions,
  selectCompletionSteps,
  selectCompletionCount,
  selectCanComplete,
} from "features/builder/slice";
import { BlocklySettingsModal } from "./BlocklySettingsModal";

const PANEL_HEIGHT = 640;
const PANEL_TOP = 160;
const PANEL_WIDTH_COLLAPSED = 80;
const PANEL_WIDTH_EXPANDED = 400;
const ICON_BOX_SIZE = 56;
const RING_CIRCUMFERENCE = 176;

const STEP_CONFIG: {
  id: CompletionStepId;
  symbol: string;
  labelKey: string;
  btnKey: string;
}[] = [
  {
    id: CompletionStepId.CHARACTER,
    symbol: "person",
    labelKey: "MSG_BUILDER_STEP_CHARACTER",
    btnKey: "MSG_BUILDER_STEP_BTN_PLACE",
  },
  {
    id: CompletionStepId.GOAL,
    symbol: "flag",
    labelKey: "MSG_BUILDER_STEP_GOAL",
    btnKey: "MSG_BUILDER_STEP_BTN_PLACE",
  },
  {
    id: CompletionStepId.BLOCKS,
    symbol: "extension",
    labelKey: "MSG_BUILDER_STEP_BLOCKS",
    btnKey: "MSG_BUILDER_STEP_BTN_SETTING",
  },
  {
    id: CompletionStepId.TESTED,
    symbol: "play_arrow",
    labelKey: "MSG_BUILDER_STEP_TEST",
    btnKey: "MSG_BUILDER_STEP_BTN_RETRY",
  },
  {
    id: CompletionStepId.META,
    symbol: "description",
    labelKey: "MSG_BUILDER_STEP_META",
    btnKey: "MSG_BUILDER_STEP_BTN_EDIT",
  },
];

export const PublishStepsPanel = ({ onTest }: { onTest?: () => void }) => {
  const play = usePlaySound();
  const { scale } = useScale();
  const dispatch = useDispatch();

  const [collapsed, setCollapsed] = useState(false);
  const [showBlocklyModal, setShowBlocklyModal] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  const selectedPlacedModelId = useSelector(
    (state: RootState) => state.builder.selectedPlacedModelId,
  );
  const steps = useSelector(selectCompletionSteps);
  const completionCount = useSelector(selectCompletionCount);
  const canComplete = useSelector(selectCanComplete);
  const enabledBlocks = useSelector((state: RootState) => state.builder.enabledBlocks);
  const status = useSelector((state: RootState) => state.builder.status);
  const draftDirty = useSelector((state: RootState) => state.builder.draftDirty);
  const pieceId = useSelector((state: RootState) => state.builder.editingBuilderMapId);
  const active = useSelector((state: RootState) => state.user.active);

  // 既存 RightPanel（アセット選択時操作）とは排他表示
  if (selectedPlacedModelId !== null) return null;

  const isCompleted = status === PieceStatus.PUBLISHED || status === PieceStatus.UNPUBLISHED;

  const handleToggle = () => {
    play();
    btnDelay(() => setCollapsed((v) => !v));
  };

  const handleExpand = () => {
    play();
    btnDelay(() => setCollapsed(false));
  };

  const handleStepAction = (id: CompletionStepId) => () => {
    play();
    btnDelay(() => {
      switch (id) {
        case CompletionStepId.BLOCKS:
          setShowBlocklyModal(true);
          break;
        case CompletionStepId.TESTED:
          onTest?.();
          break;
        default:
          toast(I18n.t(STEP_CONFIG.find((s) => s.id === id)?.labelKey ?? ""));
          break;
      }
    });
  };

  const handleComplete = () => {
    if (!canComplete) {
      toast(I18n.t("MSG_BUILDER_COMPLETE_FAILED"));
      return;
    }
    play();
    btnDelay(async () => {
      const result = await completePiece({
        piece_id: pieceId,
        uid: active.uid,
        tested: true,
      });
      dispatch(actions.setPieceStatus(result.status));
      dispatch(actions.setDraftDirty(false));
      toast(I18n.t("MSG_BUILDER_COMPLETE_TOAST"));
    });
  };

  const handlePublish = () => {
    play();
    btnDelay(async () => {
      const result = await publishPiece({ piece_id: pieceId, uid: active.uid });
      dispatch(actions.setPieceStatus(result.status));
    });
  };

  const handleDiscardDraft = () => {
    play();
    btnDelay(() => setShowDiscardConfirm(true));
  };

  const confirmDiscardDraft = async () => {
    await deleteDraft({ piece_id: pieceId, uid: active.uid });
    dispatch(actions.discardDraft());
    setShowDiscardConfirm(false);
  };

  const ringOffset = RING_CIRCUMFERENCE * (1 - completionCount / 5);

  return (
    <>
      <div
        className="flex-col-el absolute right-0 z-[9000] pointer-events-none"
        style={{ top: PANEL_TOP * scale }}
      >
        <div
          className="pointer-events-auto"
          style={{
            width: PANEL_WIDTH_EXPANDED * scale,
            height: PANEL_HEIGHT * scale,
            marginRight: 16 * scale,
          }}
        >
          {/* スケール層 */}
          <div
            className="flex-row-el items-start justify-end origin-top-left"
            style={{
              width: PANEL_WIDTH_EXPANDED,
              height: PANEL_HEIGHT,
              transform: `scale(${scale})`,
            }}
          >
            {/* パネル本体（width をアニメーション、高さは collapsed/expanded で一致） */}
            <div
              className="flex-col-el bg-white rounded-2xl overflow-hidden transition-[width] duration-300 ease-out"
              style={{
                width: collapsed ? PANEL_WIDTH_COLLAPSED : PANEL_WIDTH_EXPANDED,
                height: PANEL_HEIGHT,
              }}
            >
              {/* ヘッダー */}
              {collapsed ? (
                <div className="flex-row-el flex-center p-2">
                  <button
                    onClick={handleToggle}
                    aria-label={I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                    className="flex-col-el flex-center w-10 h-10 rounded-full bg-blue-500 border-0 type-inherit-font shadow-md active:scale-95 transition-transform"
                  >
                    <span className="flex-col-el flex-center type-md text-white">
                      <ChevronLeftIcon fontSize="inherit" />
                    </span>
                  </button>
                </div>
              ) : (
                <div className="flex-row-el items-center gap-3 p-4">
                  <div className="relative w-14 h-14 shrink-0">
                    <svg className="absolute inset-0" viewBox="0 0 64 64">
                      <circle cx="32" cy="32" r="28" fill="none" stroke="#E5E7EB" strokeWidth="6" />
                      <circle
                        cx="32"
                        cy="32"
                        r="28"
                        fill="none"
                        stroke="#2bdc98"
                        strokeWidth="6"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={ringOffset}
                        transform="rotate(-90 32 32)"
                      />
                    </svg>
                    <div className="flex-col-el flex-center absolute inset-0">
                      <span className="type-sm text-green-500">{completionCount}/5</span>
                    </div>
                  </div>
                  <div className="flex-col-el items-start flex-1 min-w-0">
                    <span className="type-body text-textcolor-black whitespace-nowrap">
                      {I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                    </span>
                    <span className="type-sm text-gray-500 whitespace-nowrap">
                      {I18n.t("MSG_BUILDER_PUBLISH_PROGRESS_DONE")}
                    </span>
                  </div>
                  <button
                    onClick={handleToggle}
                    aria-label={I18n.t("MSG_BUILDER_PUBLISH_PANEL_TITLE")}
                    className="flex-col-el flex-center shrink-0 w-10 h-10 rounded-full bg-blue-500 border-0 type-inherit-font shadow-md active:scale-95 transition-transform"
                  >
                    <span className="flex-col-el flex-center type-md text-white">
                      <ChevronRightIcon fontSize="inherit" />
                    </span>
                  </button>
                </div>
              )}

              {/* コンテンツ領域 */}
              {collapsed ? (
                <div className="flex-col-el items-center gap-2 p-2">
                  {STEP_CONFIG.map((step) => {
                    const done = steps[step.id];
                    return (
                      <button
                        key={step.id}
                        onClick={handleExpand}
                        aria-label={I18n.t(step.labelKey)}
                        className={`relative flex-col-el flex-center shrink-0 rounded-xl border-0 type-inherit-font ${
                          done ? "bg-green-500" : "bg-gray-400"
                        }`}
                        style={{ width: ICON_BOX_SIZE, height: ICON_BOX_SIZE }}
                      >
                        <span className="material-symbols-outlined type-title text-white">
                          {step.symbol}
                        </span>
                        {done && (
                          <span className="flex-col-el flex-center absolute -top-1 -right-1 w-5 h-5 rounded-full bg-white type-sm text-green-500">
                            ✓
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="flex-col-el items-stretch gap-2 flex-1 min-h-0 overflow-y-auto kid-scrollbar px-4">
                  {STEP_CONFIG.map((step) => {
                    const done = steps[step.id];
                    return (
                      <div
                        key={step.id}
                        className={`flex-row-el items-center gap-2 rounded-xl p-2 ${
                          done ? "bg-green-50" : "bg-gray-50"
                        }`}
                      >
                        <span
                          className={`flex-col-el flex-center shrink-0 type-md ${
                            done ? "text-green-500" : "text-gray-300"
                          }`}
                        >
                          {done ? (
                            <CheckCircleIcon fontSize="inherit" />
                          ) : (
                            <RadioButtonUncheckedIcon fontSize="inherit" />
                          )}
                        </span>
                        <span className="material-symbols-outlined shrink-0 type-md text-gray-600">
                          {step.symbol}
                        </span>
                        <div className="flex-col-el items-start flex-1 min-w-0">
                          <span className="type-sm text-textcolor-black whitespace-nowrap">
                            {I18n.t(step.labelKey)}
                          </span>
                          {step.id === CompletionStepId.BLOCKS && (
                            <span className="type-sm text-gray-500 whitespace-nowrap">
                              {I18n.t("MSG_BUILDER_STEP_BLOCKS_COUNT", {
                                count: enabledBlocks.length,
                              })}
                            </span>
                          )}
                        </div>
                        <div className="upsilon shrink-0">
                          <button
                            onClick={handleStepAction(step.id)}
                            className="btn btn-primary type-inherit-font type-sm w-24 px-2 py-1 whitespace-nowrap"
                          >
                            {I18n.t(step.btnKey)}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* フッター（collapsed では非表示） */}
              {!collapsed && (
                <div className="flex-col-el items-stretch gap-2 p-4">
                  {isCompleted ? (
                    <div className="alpha">
                      <button
                        onClick={handlePublish}
                        className="btn btn-primary type-inherit-font type-body w-full whitespace-nowrap"
                      >
                        <span className="flex-row-el flex-center gap-2">
                          <span className="flex-col-el flex-center type-md">
                            <ShareIcon fontSize="inherit" />
                          </span>
                          {I18n.t("MSG_BUILDER_PUBLISH_SHARE_BTN")}
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="alpha">
                      <button
                        onClick={handleComplete}
                        disabled={!canComplete}
                        className={`btn btn-primary type-inherit-font type-body w-full whitespace-nowrap ${
                          canComplete ? "" : "opacity-50"
                        }`}
                      >
                        {I18n.t("MSG_BUILDER_COMPLETE_BTN")}
                      </button>
                    </div>
                  )}

                  {draftDirty && (
                    <div className="alpha">
                      <button
                        onClick={handleDiscardDraft}
                        className="btn btn-primary type-inherit-font type-sm w-full whitespace-nowrap"
                      >
                        {I18n.t("MSG_BUILDER_DISCARD_DRAFT_BTN")}
                      </button>
                    </div>
                  )}

                  <span className="type-sm text-gray-500 text-center whitespace-nowrap">
                    {I18n.t("MSG_BUILDER_PUBLISH_HINT")}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <BlocklySettingsModal open={showBlocklyModal} onClose={() => setShowBlocklyModal(false)} />

      {showDiscardConfirm && (
        <ConfirmMessageBox
          title={I18n.t("MSG_BUILDER_DISCARD_DRAFT_CONFIRM")}
          message={I18n.t("MSG_BUILDER_DISCARD_DRAFT_BTN")}
          close={() => setShowDiscardConfirm(false)}
          confirm={confirmDiscardDraft}
        />
      )}
    </>
  );
};
